import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { deleteDraftPrompt, getDraftPrompt, putDraftPrompt } from '@/api/prompts'
import { Banner } from '@/components/Banner'
import { STALE_DRAFT_SAVE_REASON, StaleDraftBanner } from '@/components/StaleDraftBanner'
import { Tooltip } from '@/components/Tooltip'
import { useServerSeed } from '@/hooks/useServerSeed'
import { errorMessage } from '@/lib/apiErrors'
import type { PromptDeleteState } from '../promptDeleteState'

export interface PromptFileRowProps {
  filename: string
  /** Human-readable note on what currently points at this file, if anything
   * ("Product baseline: memorandum_of_sale_data_extract", "Unreferenced",
   * etc.) - shown as a warning before Delete when the file is referenced. */
  usedBy: string
  isReferenced: boolean
  /** Which delete action applies (promptDeleteState.ts) - decides the
   * button's label and whether it is enabled. */
  deleteState: PromptDeleteState
  /** Called after a successful save or delete, so the parent can refetch the
   * raw file list. */
  onChanged: () => void
}

// One row's expanded body - a direct editor over the raw prompts/*.txt file
// content itself (PRD 29), independent of whichever product/document type
// happens to reference it. Editing here never touches product_types.json or
// document_types.json - if this file is already referenced by one of them,
// that reference is unaffected by a content edit (only Delete would leave it
// dangling, which is called out inline before the operator confirms it).
export function PromptFileRow({ filename, usedBy, isReferenced, deleteState, onChanged }: PromptFileRowProps) {
  const queryKey = ['draft-prompt', filename]
  const promptQuery = useQuery({
    queryKey,
    queryFn: () => getDraftPrompt(filename),
  })

  const [content, setContent] = useState('')
  const [savedContent, setSavedContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Seeds the textarea and follows server changes (hooks/useServerSeed.ts):
  // a revert here, a discard in Diff & Validation, publish.
  const serverSeed = useServerSeed(queryKey, promptQuery, {
    seed: (data) => {
      setContent(data.content)
      setSavedContent(data.content)
    },
    isDirty: () => savedContent !== null && content !== savedContent,
  })
  if (savedContent === null && promptQuery.isError) {
    setSavedContent('')
  }

  const isDirty = savedContent !== null && content !== savedContent
  const trimmed = content.trim()

  async function handleSave() {
    if (trimmed.length === 0) return
    setError(null)
    if (!(await serverSeed.ensureCurrent())) return
    setIsSaving(true)
    try {
      await putDraftPrompt(filename, content)
      setSavedContent(content)
      serverSeed.noteSaved({ filename, content })
      onChanged()
    } catch (err) {
      setError(errorMessage(err) ?? 'Failed to save this prompt file')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleDelete() {
    setError(null)
    setIsDeleting(true)
    try {
      await deleteDraftPrompt(filename)
      // Reverting brings the live text back: the refresh refetches it and
      // useServerSeed re-seeds the textarea (any unsaved typing is dropped
      // with the draft, as the operator asked). A draft-only file just
      // disappears from the list.
      serverSeed.reload()
      onChanged()
    } catch (err) {
      setError(errorMessage(err) ?? 'Failed to delete this prompt file')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 bg-surface p-4">
      {serverSeed.baseChanged && <StaleDraftBanner onReload={serverSeed.reload} />}
      {error && (
        <Banner variant="danger" icon={AlertCircle} title="Failed">
          {error}
        </Banner>
      )}
      {isReferenced && (
        <Banner variant="warning" icon={AlertCircle} title="This file is in use">
          {usedBy}. Deleting it will leave that reference pointing at a missing file.
        </Banner>
      )}
      {promptQuery.isLoading ? (
        <p className="text-meta text-text-secondary">Loading…</p>
      ) : (
        <textarea
          rows={10}
          value={content}
          onChange={(event) => setContent(event.target.value)}
          className="rounded-control border border-border bg-surface px-3 py-2 font-mono text-meta text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
        />
      )}
      <div className="flex justify-end gap-2">
        {deleteState === 'published-unchanged' ? (
          // aria-disabled rather than disabled, so hover and keyboard focus
          // still reach the Tooltip (same pattern as ExtractorsScreen's Save).
          <Tooltip text="You can't delete a published prompt.">
            <button
              type="button"
              aria-disabled="true"
              className="cursor-not-allowed rounded-control border border-danger px-4 py-2 text-body font-medium text-danger opacity-50"
            >
              Delete file
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            disabled={deleteState === 'unknown' || isSaving || isDeleting}
            onClick={() => void handleDelete()}
            className="rounded-control border border-danger px-4 py-2 text-body font-medium text-danger disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteState === 'published-modified'
              ? isDeleting
                ? 'Reverting…'
                : 'Revert to published'
              : isDeleting
                ? 'Deleting…'
                : 'Delete file'}
          </button>
        )}
        {(() => {
          const button = (
            <button
              type="button"
              disabled={!isDirty || serverSeed.baseChanged || trimmed.length === 0 || isSaving || isDeleting}
              onClick={() => void handleSave()}
              className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          )
          return serverSeed.baseChanged ? <Tooltip text={STALE_DRAFT_SAVE_REASON}>{button}</Tooltip> : button
        })()}
      </div>
    </div>
  )
}
